import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ForgotPassword from "../pages/ForgotPassword";
import ResetPassword from "../pages/ResetPassword";
import ChangedPassword from "../pages/ChangedPassword";
import OtpVerification from "../pages/Signup/OtpVerification";
import JoinWorkspace from "../pages/Signup/JoinWorkspace";
import CreateWorkspace from "../pages/Signup/CreateWorkspace";
import CreatePassword from "../pages/Signup/CreatePassword";
import PasswordCreated from "../pages/Signup/PasswordCreated";
import CreateAgentName from "../pages/Signup/CreateAgentName";
import ProtectedRoute from "../ProtectedRoute";
import TermsOfService from "../pages/Signup/TermsOfService/index.jsx";
import { isFullyAuthenticated } from "../utils/helpers/authCookies";
import {
  getPracticeSpecialty,
  shouldSkipSpecialtySelect,
} from "../utils/helpers/practiceSpecialty";
import AgencyQrCode from "../pages/AgencyQrCode/index.jsx";
import PatientDetails from "../pages/PatientDetails/index.jsx";
import SetupPassword from "../pages/SetupPassword/index.jsx";
import DownloadBiosynciqApp from "../pages/DownloadBiosynciqApp/index.jsx";
import NotFound from "../pages/NotFound/index.jsx";
import CarePlanLayout from "../components/CarePlan/CarePlanLayout/index.jsx";
import RequestAccountDeletion from "../pages/RequestAccountDeletion/index.jsx";
import { useSelector } from "react-redux";

const LazyRoute = ({ children }) => (
  <Suspense fallback={null}>{children}</Suspense>
);

const Signup = lazy(() => import("../pages/Signup"));
const Signin = lazy(() => import("../pages/Signin"));
const Patients = lazy(() => import("../pages/Patients"));
const Profile = lazy(() => import("../pages/Profile"));
const Settings = lazy(() => import("../pages/Settings"));
const DashboardComponent = lazy(() => import("../components/Dashboard"));
const CaseOverview = lazy(() =>
  import("../pages/Patients/PatientOverview/index.js")
);
const AccountType = lazy(() => import("../pages/Signup/AccountType/index.jsx"));
const SignUpCareManagement = lazy(() =>
  import("../pages/Signup/SignUpCareManagement/index.jsx")
);
const ImportPatients = lazy(() => import("../pages/ImportPatients/index.jsx"));
const OrganizationPortal = lazy(() =>
  import("../pages/OrganizationPortal/index.jsx")
);
const BillingCardDetails = lazy(() =>
  import("../pages/Signup/BillingCardDetails/index.jsx")
);
const AssignPatients = lazy(() => import("../pages/AssignPatients/index.jsx"));
const Reports = lazy(() => import("../pages/Reports/index.jsx"));
const CarePlanOverview = lazy(() =>
  import("../pages/CarePlanWorkspace/CarePlanOverview/index.jsx")
);
const CarePlanAssessment = lazy(() =>
  import("../pages/CarePlanWorkspace/CarePlanAssessment/index.jsx")
);
const CarePlanProblems = lazy(() =>
  import("../pages/CarePlanWorkspace/CarePlanProblems/index.jsx")
);
const CarePlanGoals = lazy(() =>
  import("../pages/CarePlanWorkspace/CarePlanGoals/index.jsx")
);
const CarePlanInterventions = lazy(() =>
  import("../pages/CarePlanWorkspace/CarePlanInterventions/index.jsx")
);
const CarePlanMedications = lazy(() =>
  import("../pages/CarePlanWorkspace/CarePlanMedications/index.jsx")
);
const CarePlanCareTeam = lazy(() =>
  import("../pages/CarePlanWorkspace/CarePlanCareTeam/index.jsx")
);
const CarePlanCaregiver = lazy(() =>
  import("../pages/CarePlanWorkspace/CarePlanCaregiver/index.jsx")
);
const CarePlanVersionHistory = lazy(() =>
  import("../pages/CarePlanWorkspace/CarePlanVersionHistory/index.jsx")
);
const ImportExportWizard = lazy(() =>
  import("../pages/ImportExportWizard/index.jsx")
);
const ExportPatients = lazy(() => import("../pages/ExportPatients/index.jsx"));
const ImportExportHistory = lazy(() =>
  import("../pages/ImportExportHistory/index.jsx")
);
const ImportHistoryOverview = lazy(() =>
  import("../pages/ImportHistoryOverview/index.jsx")
);
const SeedingEntry = lazy(() => import("../pages/seeding_entry/index.jsx"));
const PracticeTerms = lazy(() => import("../pages/PracticeTerms/index.jsx"));
const Help = lazy(() => import("../pages/Help/index.jsx"));
const SupportReply = lazy(() => import("../pages/SupportReply/index.jsx"));
const SpecialtySelect = lazy(() =>
  import("../pages/SpecialtySelect/index.js")
);
const RegisteredPractitioners = lazy(() =>
  import("../pages/Check/RegisteredPractitioners/index.jsx")
);

const MainRouts = () => {
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const isAuthenticated = isFullyAuthenticated(userInfo);

  return (
    <BrowserRouter>
      <Routes>
          <Route element={<ProtectedRoute isAuthenticated={isAuthenticated} />}>
            <Route
              exact
              path="/"
              element={isAuthenticated ? <DashboardComponent /> : <Signin />}
            />
            <Route exact path="/patients" element={<Patients />} />
            <Route exact path="/patient/:id" element={<CaseOverview />} />
            <Route exact path="/assign-patients" element={<AssignPatients />} />
            <Route exact path="/reports" element={<Reports />} />
            <Route
              exact
              path="/import-export-wizard"
              element={<ImportExportWizard />}
            />
            <Route exact path="/import-patients" element={<ImportPatients />} />
            <Route exact path="/export-patients" element={<ExportPatients />} />
            <Route
              exact
              path="/import-export-history"
              element={<ImportExportHistory />}
            />
            <Route
              exact
              path="/import-export-history/:id"
              element={<ImportHistoryOverview />}
            />
            {/* <Route exact path="/seeding-entry" element={<SeedingEntry />} /> */}
            <Route exact path="/profile" element={<Profile />} />
            <Route exact path="/settings" element={<Settings />} />
            <Route exact path="/practice-terms" element={<PracticeTerms />} />
            <Route exact path="/help" element={<Help />} />
            <Route
              exact
              element={isAuthenticated ? <OrganizationPortal /> : <Signin />}
              path="/organization-portal"
            />
          </Route>

          <Route
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                layout={CarePlanLayout}
              />
            }
          >
            <Route path="/patient/:id/care-plan-workspace">
              <Route path="overview" element={<CarePlanOverview />} />
              <Route path="assessment" element={<CarePlanAssessment />} />
              <Route path="problems" element={<CarePlanProblems />} />
              <Route path="goals" element={<CarePlanGoals />} />
              <Route path="interventions" element={<CarePlanInterventions />} />
              <Route path="medications" element={<CarePlanMedications />} />
              <Route path="care-team" element={<CarePlanCareTeam />} />
              <Route path="caregiver" element={<CarePlanCaregiver />} />
              <Route
                path="version-history"
                element={<CarePlanVersionHistory />}
              />
            </Route>
          </Route>

          <Route path="/practice_detail" element={<AgencyQrCode />} />
          <Route path="/patient-details" element={<PatientDetails />} />
          <Route path="/setup-password" element={<SetupPassword />} />
          <Route
            path="/download-mobile-app"
            element={<DownloadBiosynciqApp />}
          />
          <Route
            path="/request-account-deletion"
            element={<RequestAccountDeletion />}
          />
          <Route
            path="/select-specialty"
            element={
              isAuthenticated ? (
                <LazyRoute>
                  <SpecialtySelect />
                </LazyRoute>
              ) : (
                <Navigate to="/signin" replace />
              )
            }
          />
          <Route
            path="/signin"
            element={
              isAuthenticated ? (
                <Navigate
                  to={
                    getPracticeSpecialty() || shouldSkipSpecialtySelect()
                      ? "/"
                      : "/select-specialty"
                  }
                  replace
                />
              ) : (
                <LazyRoute>
                  <Signin />
                </LazyRoute>
              )
            }
          />
          <Route
            path="/forgot-password"
            element={
              isAuthenticated ? (
                <Navigate to="/" />
              ) : (
                <ForgotPassword />
              )
            }
          />
          <Route path="/password-changed" element={<ChangedPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/support-reply"
            element={
              <LazyRoute>
                <SupportReply />
              </LazyRoute>
            }
          />
          <Route
            path="/check/registered-practitioners"
            element={
              <LazyRoute>
                <RegisteredPractitioners />
              </LazyRoute>
            }
          />
          <Route
            path="/account-type"
            element={
              isAuthenticated ? (
                <Navigate to="/" />
              ) : (
                <LazyRoute>
                  <AccountType />
                </LazyRoute>
              )
            }
          />
          <Route
            path="/register"
            element={
              isAuthenticated ? (
                <Navigate to="/" />
              ) : (
                <LazyRoute>
                  <Signup />
                </LazyRoute>
              )
            }
          />
          <Route
            path="/register-care-management"
            element={
              isAuthenticated ? (
                <Navigate to="/" />
              ) : (
                <LazyRoute>
                  <SignUpCareManagement />
                </LazyRoute>
              )
            }
          />
          <Route path="/create-password" element={<CreatePassword />} />
          <Route path="/otp-verification" element={<OtpVerification />} />
          <Route path="/join-workspace" element={<JoinWorkspace />} />
          <Route path="/create-workspace" element={<CreateWorkspace />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/create-agentname" element={<CreateAgentName />} />
          <Route
            path="/billing-details"
            element={
              <LazyRoute>
                <BillingCardDetails />
              </LazyRoute>
            }
          />
          <Route path="/password-created" element={<PasswordCreated />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
    </BrowserRouter>
  );
};
export default MainRouts;
